import React, { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import AppLayout from "@amzn/awsui-components-react/polaris/app-layout";
import {
  Alert,
  Badge,
  Box,
  BreadcrumbGroup,
  Button,
  ContentLayout,
  Flashbar,
  FlashbarProps,
  Grid,
  Header,
  Icon,
  Link,
  SpaceBetween,
  Spinner,
  Tabs,
} from "@amzn/awsui-components-react";
import * as tokens from "@amzn/awsui-design-tokens-core";
import PageHeader from "../PageHeader";
import DarkModeContext from "../../contexes/DarkModeContext";
import { StageContext } from "../../contexes/OrderIdContext";
import { DARK_THEME } from "../../constants";
import { Stage } from "../../lusca-search-api/generated-src";
import {
  useInvestigation,
  useInvestigationSuggestions,
  usePrefetchInvestigationItems,
  useUpdateInvestigation,
} from "../../hooks/useInvestigations";
import {
  rememberWidenPromptDismissed,
  widenActionCount,
  widenPromptWasDismissed,
} from "./WidenRuleModal";
import useCurrentUser from "../../hooks/useCurrentUser";
import useDocumentTitle from "../../hooks/useDocumentTitle";
import useInvestigationsNav from "../../hooks/useInvestigationsNav";
import { CopyToClipboard } from "../common/CopyToClipboard";
import { InvestigationOwnerField } from "./InvestigationOwnerField";
import { InvestigationOwnerBanner } from "./InvestigationOwnerBanner";
import { InvestigationStatus } from "./InvestigationStatus";
import { InvestigationsSideNav } from "./InvestigationsSideNav";
import { InvestigationComments } from "./InvestigationComments";
import { InvestigationConclusion } from "./InvestigationConclusion";
import { useNewTrackStarsRun } from "./useNewTrackStarsRun";
import { InvestigationItems } from "./InvestigationItems";
import { splitListBlocker } from "./InvestigationSplitAction";
import { InvestigationSplitListModal } from "./InvestigationSplitListModal";
import { InvestigationRedrivePanel } from "./InvestigationRedrivePanel";
import { InvestigationTimeline } from "./InvestigationTimeline";
import { InvestigationMoreActions } from "./InvestigationMoreActions";
import { InvestigationFollowButton } from "./InvestigationFollowButton";
import { InvestigationReminderButton } from "./InvestigationReminderButton";
import {
  InvestigationTaskAction,
  InvestigationTaskDriftBanner,
} from "./InvestigationTaskAction";
import { InvestigationTitle } from "./InvestigationTitle";
import { useMarkCaseNotificationsRead } from "./NotificationsPanel";
import { InvestigationRule } from "./InvestigationRule";
import { useErrorCategorySources } from "./CategorySource";
import { InvestigationRelated, relationsFromIds } from "./InvestigationRelated";
import { InvestigationAthenaQuery } from "./InvestigationAthenaQuery";
import "./RuleBlock.scss";
import { InvestigationTrendChart } from "./InvestigationTrendChart";
import {
  InvestigationPopulationCard,
  populationFacts,
} from "./InvestigationPopulationCard";
import {
  InvestigationTrackStarsTab,
  ProposedConclusion,
  RunTrackStarsButton,
  TrackStarsRunStatus,
} from "./InvestigationTrackStarsTab";
import { dashboardPathForRule } from "../dashboard/ruleFilters";
import {
  caseRules,
  commonRulePredicates,
  investigationPath,
  isTerminal,
  overviewMeta,
  predicateValueText,
} from "./investigationUtils";
import { shortPatternId } from "./trackstarsText";

/** Tab ids on this page; also the values accepted in `?tab=` (e.g. `&tab=conclusion`). */
const TAB_IDS = [
  "work",
  "trackstars",
  "items",
  "population",
  "redrive",
  "conclusion",
  "related",
  "timeline",
];
const parseTab = (value: string | null): string | null =>
  value && TAB_IDS.includes(value) ? value : null;

/** Full collaboration view at /dashboard/dlq/investigations/:investigationId */
const InvestigationPage: React.FC = () => {
  const { investigationId } = useParams<{ investigationId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const stage = (searchParams.get("stage") as Stage) || Stage.Prod;
  const [darkMode, setDarkMode] = useState(
    () => window.localStorage.getItem("theme") === DARK_THEME
  );
  const [flash, setFlash] = useState<FlashbarProps.MessageDefinition[]>([]);
  // The selected tab lives in `?tab=` so links can open a specific tab. No/unknown value =
  // status-based default below. `replace` keeps tab clicks out of the back-button history.
  const selectedTabId = parseTab(searchParams.get("tab"));
  const setActiveTabId = (tab: string) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", tab);
        return next;
      },
      { replace: true }
    );
  // A conclusion proposed from the TrackStars tab, handed to the Conclusion tab's form.
  const [proposal, setProposal] = useState<ProposedConclusion | null>(null);
  // Split by list opened from ticked items (Items tab) or returned items (Redrives tab).
  const [splitPrefill, setSplitPrefill] = useState<{
    lines: string[];
    itemsLabel?: string;
  } | null>(null);
  const update = useUpdateInvestigation(stage);
  const detail = useInvestigation(stage, investigationId || null);
  // The Items tab only mounts when selected; warm its first page in parallel with the detail
  // request so the tab is usually already loaded by the time the user clicks it.
  usePrefetchInvestigationItems(stage, investigationId || null);
  const currentUser = useCurrentUser();
  useMarkCaseNotificationsRead(currentUser, investigationId);
  const nav = useInvestigationsNav();
  // The TrackStars tab asks for the owner's attention when a newer report found more of the
  // failure this case is on (same check as the tab's "Widen rule" button).
  const suggestionsForCase = useInvestigationSuggestions(stage);
  const categorySources = useErrorCategorySources();
  const investigation = detail.data?.investigation;
  const [openWiden, setOpenWiden] = useState(false);
  const [widenPromptDismissed, setWidenPromptDismissed] = useState(() =>
    widenPromptWasDismissed(investigationId)
  );
  const dismissWidenPrompt = (id: string) => {
    rememberWidenPromptDismissed(id);
    setWidenPromptDismissed(true);
  };
  const trackStarsActions =
    investigation &&
    investigation.owner === currentUser &&
    !isTerminal(investigation.status)
      ? widenActionCount(investigation, suggestionsForCase?.data?.suggestions)
      : 0;
  // Related cases (parent, children, merges, duplicates) live in their own tab, shown only
  // when there is at least one.
  let relatedRows: ReturnType<typeof relationsFromIds> = [];
  if (detail.data?.related?.length) relatedRows = detail.data.related;
  else if (investigation) relatedRows = relationsFromIds(investigation);
  // Resolved cases open on their Conclusion, monitored ones on Redrives (where the monitoring
  // progress is); everything else opens on Comments.
  let defaultTab = "work";
  if (investigation?.status === "RESOLVED") defaultTab = "conclusion";
  else if (investigation?.status === "MONITORING_REDRIVE")
    defaultTab = "redrive";
  const activeTabId = selectedTabId ?? defaultTab;
  const newTrackStarsRun = useNewTrackStarsRun(
    investigation,
    activeTabId === "trackstars"
  );
  useDocumentTitle(
    investigation?.title ||
      (investigationId ? `Investigation ${investigationId}` : "Investigation")
  );

  const onError = (message: string) =>
    setFlash([
      {
        type: "error",
        content: message,
        dismissible: true,
        id: `${Date.now()}`,
        onDismiss: () => setFlash([]),
      },
    ]);

  return (
    <DarkModeContext.Provider value={darkMode}>
      <StageContext.Provider value={stage}>
        <PageHeader setDarkMode={setDarkMode} />
        <AppLayout
          contentType="default"
          headerSelector="#h"
          {...nav}
          maxContentWidth={1700}
          navigation={<InvestigationsSideNav stage={stage} />}
          toolsHide
          notifications={flash.length ? <Flashbar items={flash} /> : undefined}
          breadcrumbs={
            <SpaceBetween direction="horizontal" size="xxs" alignItems="center">
              <BreadcrumbGroup
                ariaLabel="Breadcrumbs"
                items={[
                  {
                    text: "DLQ Investigations",
                    href: `/dashboard/dlq/investigations?stage=${stage}`,
                  },
                  {
                    text: stage,
                    href: `/dashboard/dlq/investigations?stage=${stage}`,
                  },
                  {
                    // The id, not the title: the title is the h1 right below.
                    text: investigationId || "Investigation",
                    href: investigationId
                      ? investigationPath(investigationId, stage)
                      : "#",
                  },
                ]}
              />
              {investigationId && (
                <CopyToClipboard
                  getContent={() => Promise.resolve(investigationId)}
                  iconName="copy"
                  variant="inline-icon"
                  ariaLabel={`Copy investigation ID ${investigationId}`}
                />
              )}
            </SpaceBetween>
          }
          content={
            <ContentLayout
              header={
                investigation ? (
                  // Two fixed columns instead of Cloudscape Header's `actions` slot: that slot
                  // wraps the buttons under the title as soon as the title is long, which
                  // guessed titles ("error type on queue") almost always are. Here the title
                  // wraps within its column and the actions stay top-right. On a narrow screen
                  // the title column never shrinks below 20rem: the actions wrap under it
                  // instead of squeezing the title to one letter per line.
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "flex-start",
                      gap: "8px",
                    }}
                  >
                    <div style={{ flex: "1 1 20rem", minWidth: 0 }}>
                      <InvestigationTitle
                        stage={stage}
                        investigation={investigation}
                        currentUser={currentUser}
                        onError={onError}
                        description={
                          <SpaceBetween
                            direction="horizontal"
                            size="xs"
                            alignItems="center"
                          >
                            {/* Status is the Status card; id and stage are in the breadcrumb.
                                Only situational flags are shown here (staleness sits by Owner).
                                Redrive attempts and returns are not: the Status card's stepper
                                and "Redrive already attempted" alert already say both. */}
                            {investigation.origin?.source === "trackstars" && (
                              <Button
                                variant="inline-link"
                                onClick={() => setActiveTabId("trackstars")}
                              >
                                <Badge color="blue">
                                  From TrackStars{" "}
                                  {shortPatternId(
                                    investigation.origin.patternId,
                                    investigation.origin.reportId
                                  )}
                                </Badge>
                              </Button>
                            )}
                            {investigation.assignment?.status === "PENDING" && (
                              <Badge color="blue">Assigning items…</Badge>
                            )}
                            {investigation.mergePending && (
                              <Badge color="blue">Merging items…</Badge>
                            )}
                          </SpaceBetween>
                        }
                      />
                    </div>
                    {/* Matches the vertical offset Header gives its own actions against an h1 line. */}
                    <div style={{ flexShrink: 0, paddingBlock: "2px" }}>
                      <SpaceBetween direction="horizontal" size="xs">
                        <InvestigationFollowButton
                          stage={stage}
                          investigation={investigation}
                          currentUser={currentUser}
                          onError={onError}
                        />
                        <InvestigationReminderButton
                          stage={stage}
                          investigation={investigation}
                          onError={onError}
                        />
                        <InvestigationTaskAction
                          stage={stage}
                          investigation={investigation}
                          currentUser={currentUser}
                          onError={onError}
                        />
                        <RunTrackStarsButton
                          stage={stage}
                          investigation={investigation}
                        />
                        <InvestigationMoreActions
                          stage={stage}
                          investigation={investigation}
                          currentUser={currentUser}
                          dashboardLinks={caseRules(investigation).map(
                            (branch, i, all) => ({
                              text:
                                all.length === 1
                                  ? "View in dashboard"
                                  : `View branch ${i + 1} in dashboard`,
                              // Full branch predicate as a hover tooltip, so the menu stays narrow.
                              description:
                                all.length === 1
                                  ? undefined
                                  : branch
                                      .map((p) => predicateValueText(p, "|"))
                                      .join(" · "),
                              href: dashboardPathForRule(stage, branch),
                            })
                          )}
                          onMerged={() => {
                            detail.refetch().catch(console.error);
                          }}
                        />
                      </SpaceBetween>
                    </div>
                  </div>
                ) : (
                  <Header variant="h1">Investigation</Header>
                )
              }
            >
              {detail.isLoading && (
                <Box textAlign="center" padding="xxl">
                  <Spinner size="large" />
                </Box>
              )}
              {detail.error && (
                <Alert type="error" header="Could not load investigation">
                  {detail.error.status === 404
                    ? "This investigation does not exist for the selected stage."
                    : detail.error.message}
                </Alert>
              )}
              {investigation && (
                <SpaceBetween size="l">
                  <TrackStarsRunStatus
                    investigation={investigation}
                    pendingOnly
                  />
                  {investigation.mergedIntoInvestigationId && (
                    <Alert
                      type="info"
                      header="This investigation was merged into another case"
                      data-testid="merged-into-banner"
                    >
                      Its items and rule now belong to{" "}
                      <Link
                        href={investigationPath(
                          investigation.mergedIntoInvestigationId,
                          stage
                        )}
                      >
                        {detail.data?.related?.find(
                          (r) => r.relation === "merged_into"
                        )?.title ?? investigation.mergedIntoInvestigationId}
                      </Link>
                      . This page is kept for its history and cannot be
                      reopened; split the survivor if part of it needs its own
                      case again.
                    </Alert>
                  )}
                  {investigation.duplicateOfInvestigationId && (
                    <Alert
                      type="info"
                      header="This investigation duplicated an existing case"
                      data-testid="duplicate-of-banner"
                    >
                      Every item in its population was already owned by{" "}
                      <Link
                        href={investigationPath(
                          investigation.duplicateOfInvestigationId,
                          stage
                        )}
                      >
                        {detail.data?.related?.find(
                          (r) => r.relation === "duplicate_of"
                        )?.title ?? investigation.duplicateOfInvestigationId}
                      </Link>
                      , so Lusca closed it as soon as it was created and nothing
                      was claimed. Work on that investigation instead; this page
                      is kept for its history and cannot be reopened.
                    </Alert>
                  )}
                  <InvestigationTaskDriftBanner
                    stage={stage}
                    investigation={investigation}
                    currentUser={currentUser}
                    onError={onError}
                  />
                  {trackStarsActions > 0 && !widenPromptDismissed && (
                    <Alert
                      type="info"
                      dismissible
                      onDismiss={() => dismissWidenPrompt(investigation.id)}
                      header="TrackStars found more of this failure"
                      action={
                        <Button
                          onClick={() => {
                            setActiveTabId("trackstars");
                            setOpenWiden(true);
                          }}
                        >
                          Review rule change…
                        </Button>
                      }
                    >
                      A newer report matches items your rule leaves out. Widen
                      the rule so they join this investigation.
                    </Alert>
                  )}
                  {!investigation.mergedIntoInvestigationId &&
                    !investigation.duplicateOfInvestigationId && (
                      <InvestigationOwnerBanner
                        stage={stage}
                        investigation={investigation}
                        onError={onError}
                      />
                    )}
                  {/*
                    Overview and Status side by side: the two things a visitor reads first,
                    each in its own column so neither stretches across the whole page.
                    Stacks below the `m` breakpoint.
                  */}
                  {investigation.description && (
                    <Box variant="p" margin="n">
                      {investigation.description}
                    </Box>
                  )}
                  {/*
                    Where the case stands (Status, wide horizontal stepper, then the population
                    rule) beside a Taskei-style details panel: owner, how big the population is,
                    when it was created. Related cases have their own tab below.
                  */}
                  <Grid
                    gridDefinition={[
                      { colspan: { default: 12, m: 7 } },
                      { colspan: { default: 12, m: 5 } },
                    ]}
                  >
                    <InvestigationStatus
                      stage={stage}
                      investigation={investigation}
                      currentUser={currentUser}
                      onError={onError}
                      related={detail.data?.related}
                      onViewRedrives={() => setActiveTabId("redrive")}
                      onOpenRedrive={(action) =>
                        setSearchParams(
                          (prev) => {
                            const next = new URLSearchParams(prev);
                            next.set("tab", "redrive");
                            next.set("redrive", action);
                            return next;
                          },
                          { replace: true }
                        )
                      }
                      orientation="horizontal"
                      footer={
                        // The rule defines the case, so it stays in view under the status.
                        <div>
                          <Box
                            variant="awsui-key-label"
                            margin={{ bottom: "xxs" }}
                          >
                            Population rule
                          </Box>
                          <div
                            className="lusca-rule-block"
                            style={{
                              background: tokens.colorBackgroundCellShaded,
                              border: `1px solid ${tokens.colorBorderDividerSecondary}`,
                            }}
                          >
                            <InvestigationRule
                              rules={caseRules(investigation)}
                              categoryScope={investigation.categoryScope}
                              categorySources={categorySources.data}
                            />
                            <div className="lusca-rule-block__tools">
                              <InvestigationAthenaQuery
                                stage={stage}
                                rule={caseRules(investigation)}
                                queryExecutionId={
                                  investigation.population?.queryExecutionId
                                }
                                frozenAt={investigation.population?.frozenAt}
                              />
                            </div>
                          </div>
                        </div>
                      }
                    />
                    <InvestigationPopulationCard
                      investigation={investigation}
                      stage={stage}
                      onViewTrend={() => setActiveTabId("population")}
                      onViewRelated={() => setActiveTabId("related")}
                      related={detail.data?.related}
                      top={
                        <InvestigationOwnerField
                          stage={stage}
                          investigation={investigation}
                          currentUser={currentUser}
                          onError={onError}
                        />
                      }
                      bottom={
                        <Box variant="small" color="text-body-secondary">
                          <SpaceBetween direction="horizontal" size="m">
                            {overviewMeta(investigation)
                              .filter((m) => m.label !== "Snapshot")
                              .map((m) => (
                                <span key={m.label} title={m.full}>
                                  {m.label} <strong>{m.value}</strong>
                                </span>
                              ))}
                          </SpaceBetween>
                        </Box>
                      }
                    />
                  </Grid>
                  <Tabs
                    activeTabId={activeTabId}
                    onChange={(event) =>
                      setActiveTabId(event.detail.activeTabId)
                    }
                    tabs={[
                      {
                        id: "work",
                        label: "Comments",
                        content: (
                          <InvestigationComments
                            stage={stage}
                            investigationId={investigation.id}
                            currentUser={currentUser}
                            owner={investigation.owner ?? undefined}
                            status={investigation.status}
                            initialEvents={detail.data?.timeline?.events || []}
                            initialNextToken={detail.data?.timeline?.nextToken}
                            onError={onError}
                          />
                        ),
                      },
                      {
                        id: "trackstars",
                        label: (
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                            }}
                          >
                            <Icon name="gen-ai" />
                            TrackStars
                            {trackStarsActions > 0 ? (
                              <span title="A newer TrackStars report found more of this failure: widen the rule to include it">
                                <Badge color="blue">{trackStarsActions}</Badge>
                              </span>
                            ) : (
                              newTrackStarsRun && (
                                <span title="A TrackStars run finished since you last opened this tab">
                                  <Badge color="blue">New</Badge>
                                </span>
                              )
                            )}
                          </span>
                        ),
                        content: (
                          <InvestigationTrackStarsTab
                            stage={stage}
                            investigation={investigation}
                            openWiden={openWiden}
                            onWidenOpened={() => setOpenWiden(false)}
                            loading={update.isLoading}
                            currentUser={currentUser}
                            onRename={(title) => {
                              update
                                .mutateAsync({
                                  investigationId: investigation.id,
                                  request: {
                                    title,
                                    expectedVersion: investigation.version,
                                  },
                                })
                                .catch((err: { message: string }) =>
                                  onError(err.message)
                                );
                            }}
                            onAddLink={(link) => {
                              update
                                .mutateAsync({
                                  investigationId: investigation.id,
                                  request: {
                                    addLink: link,
                                    expectedVersion: investigation.version,
                                  },
                                })
                                .catch((err: { message: string }) =>
                                  onError(err.message)
                                );
                            }}
                            onProposeConclusion={(next) => {
                              setProposal({ ...next });
                              setActiveTabId("conclusion");
                            }}
                          />
                        ),
                      },
                      {
                        id: "items",
                        label: `Items (${populationFacts(
                          investigation
                        ).items.toLocaleString()})`,
                        content: (
                          <InvestigationItems
                            stage={stage}
                            investigationId={investigation.id}
                            rule={commonRulePredicates(investigation)}
                            investigation={investigation}
                            onSplitSelected={
                              isTerminal(investigation.status)
                                ? undefined
                                : (lines) => setSplitPrefill({ lines })
                            }
                            splitDisabledReason={splitListBlocker(
                              investigation,
                              currentUser
                            )}
                          />
                        ),
                      },
                      {
                        id: "population",
                        label: "Population",
                        content: (
                          <SpaceBetween size="l">
                            <InvestigationTrendChart
                              investigation={investigation}
                            />
                            <InvestigationRedrivePanel
                              section="population"
                              stage={stage}
                              investigation={investigation}
                              currentUser={currentUser}
                              onError={onError}
                            />
                          </SpaceBetween>
                        ),
                      },
                      {
                        id: "redrive",
                        label: `Redrives (${
                          investigation.redriveAttemptCount ?? 0
                        })`,
                        content: (
                          <InvestigationRedrivePanel
                            section="redrives"
                            stage={stage}
                            investigation={investigation}
                            currentUser={currentUser}
                            onError={onError}
                            onSplitItems={
                              isTerminal(investigation.status)
                                ? undefined
                                : (lines, itemsLabel) =>
                                    setSplitPrefill({ lines, itemsLabel })
                            }
                            splitDisabledReason={splitListBlocker(
                              investigation,
                              currentUser
                            )}
                          />
                        ),
                      },
                      {
                        id: "conclusion",
                        label: "Conclusion",
                        content: (
                          <InvestigationConclusion
                            stage={stage}
                            investigation={investigation}
                            currentUser={currentUser}
                            onError={onError}
                            proposal={proposal}
                          />
                        ),
                      },
                      ...(relatedRows.length > 0
                        ? [
                            {
                              id: "related",
                              label: `Linked cases (${relatedRows.length})`,
                              content: (
                                <InvestigationRelated
                                  investigation={investigation}
                                  related={relatedRows}
                                  stage={stage}
                                />
                              ),
                            },
                          ]
                        : []),
                      {
                        id: "timeline",
                        label: "Audit log",
                        content: (
                          <InvestigationTimeline
                            stage={stage}
                            investigationId={investigation.id}
                            initialEvents={detail.data?.timeline?.events || []}
                            initialNextToken={detail.data?.timeline?.nextToken}
                          />
                        ),
                      },
                    ]}
                  />
                  {splitPrefill && (
                    <InvestigationSplitListModal
                      stage={stage}
                      investigation={investigation}
                      initialLines={splitPrefill.lines}
                      itemsLabel={splitPrefill.itemsLabel}
                      onClose={() => setSplitPrefill(null)}
                    />
                  )}
                </SpaceBetween>
              )}
            </ContentLayout>
          }
        />
      </StageContext.Provider>
    </DarkModeContext.Provider>
  );
};

export default InvestigationPage;
